import { useState } from "react";
import axios from "axios";
import LegalPage from "@/components/legal/LegalPage";
import { useAuth, formatAuthError } from "@/context/AuthContext";

// Style constants — mirrored verbatim from TermsOfService / PrivacyPolicy /
// RefundPolicy / SubscriptionTerms / MyAccount / PlanPricing so this page
// renders identically inside the shared LegalPage shell. No new CSS, no
// new components, no layout tweaks.
const H2      = { color: "#f7f7f7", fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 10, letterSpacing: "0.2px" };
const P       = { margin: "0 0 12px 0" };
const LABEL   = { display: "block", color: "#9a9a9a", fontSize: 12, letterSpacing: "0.3px", marginBottom: 6 };
const INPUT   = { width: "100%", background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 6, color: "#f7f7f7", padding: "10px 12px", fontSize: 14, marginBottom: 12 };
const BUTTON  = { background: "#C9A86A", color: "#0f0f0f", fontWeight: 700, fontSize: 13, letterSpacing: "0.5px", padding: "10px 18px", borderRadius: 6, border: "none", cursor: "pointer" };
const SUCCESS = { color: "#7ec97e" };
const ERROR   = { color: "#e07070" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePassword() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy]       = useState(false);
  const [ok, setOk]           = useState("");
  const [err, setErr]         = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setOk(""); setErr("");
    if (!current || !next || !confirm) { setErr("All fields are required."); return; }
    if (next.length < 8)               { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm)              { setErr("New passwords do not match."); return; }
    if (!user?.email)                  { setErr("You must be signed in."); return; }
    setBusy(true);
    try {
      // Verify current password via the existing /login endpoint.
      // (No new backend logic — reuses the live auth endpoint.)
      try {
        await axios.post(`${API}/auth/login`, { email: user.email, password: current });
      } catch (loginErr) {
        setErr("Your current password is incorrect.");
        setBusy(false);
        return;
      }
      // Trigger the existing password-reset flow — emails the user a
      // one-time reset link, which lands on /auth/reset to complete
      // the change via the existing token-based reset endpoint.
      await axios.post(`${API}/auth/reset-request`, { email: user.email });
      setOk("A password reset link has been sent to your email. Follow the link to complete your password change.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (submitErr) {
      setErr(formatAuthError(submitErr));
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalPage title="Change Password" testid="account-change-password">
      <h2 style={H2}>Instructions</h2>
      <p style={P}>Use this page to update your account password. Enter your current password and choose a new one.</p>

      <h2 style={H2}>Password Update</h2>
      <form onSubmit={onSubmit} data-testid="account-change-password-form">
        <label style={LABEL} htmlFor="cpw-current">Current password</label>
        <input
          id="cpw-current"
          data-testid="account-change-password-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          style={INPUT}
        />
        <label style={LABEL} htmlFor="cpw-new">New password</label>
        <input
          id="cpw-new"
          data-testid="account-change-password-new"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          style={INPUT}
        />
        <p style={P} data-testid="account-change-password-hint">Password must be at least 8 characters and include a mix of letters, numbers, and symbols.</p>
        <label style={LABEL} htmlFor="cpw-confirm">Confirm new password</label>
        <input
          id="cpw-confirm"
          data-testid="account-change-password-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={INPUT}
        />
        {err ? <p style={{ ...P, ...ERROR }} data-testid="account-change-password-error">{err}</p> : null}
        {ok  ? <p style={{ ...P, ...SUCCESS }} data-testid="account-change-password-success">{ok}</p> : null}
        <p style={P}>
          <button
            type="submit"
            disabled={busy}
            data-testid="account-change-password-submit"
            style={{ ...BUTTON, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
          >{busy ? "Submitting…" : "Submit"}</button>
        </p>
      </form>

      <h2 style={H2}>Notes</h2>
      <p style={P}>If you have trouble updating your password, you can contact support from the Account menu.</p>
    </LegalPage>
  );
}
